import { useTranslation } from "react-i18next";
import type { AccessList } from "../../../api/access/accessModel";
import { inclusionChange, type AccessChanges, type GroupNamer } from "../../../features/access";
import AppButton from "../../../ui/buttons/AppButton";
import DropdownChevron from "../../../ui/overlays/DropdownChevron";
import MenuButton from "../../../ui/overlays/MenuButton";

interface NewGroupButtonProps {
  list: AccessList;
  namer: GroupNamer;
  changes: AccessChanges;
  onNew: () => void;
}

/**
 * "New group" above a project's list. Where the installation has groups the project could
 * include, the same button lists them under "From the installation" (GS-1): one choice
 * includes one, as the installation sets it.
 */
export default function NewGroupButton({ list, namer, changes, onNew }: NewGroupButtonProps) {
  const { t } = useTranslation();
  const projectId = list.projectId;
  if (projectId === null || list.includableGroupIds.length === 0) {
    return <AppButton variant="secondary" size="sm" onClick={onNew}>{t("access.newGroupButton")}</AppButton>;
  }
  return (
    <MenuButton
      align="right"
      sections={[
        { id: "new", items: [{ id: "new", label: t("access.newGroupItem"), action: { kind: "select", onSelect: onNew } }] },
        {
          id: "include",
          heading: t("access.fromInstallation"),
          items: list.includableGroupIds.map((groupId) => ({
            id: groupId,
            label: namer.name(groupId),
            action: { kind: "select", onSelect: () => { changes.run(inclusionChange(t, projectId, { groupId, name: namer.name(groupId) })); } },
          })),
        },
      ]}
      renderTrigger={(trigger, open) => (
        <AppButton {...trigger} variant="secondary" size="sm">
          {t("access.newGroupButton")}
          <DropdownChevron open={open} />
        </AppButton>
      )}
    />
  );
}
